import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const BASE = process.env.REACT_APP_API_URL || '/api';
const POLL_MS = 6000;

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

export default function ChefView() {
  const { businessId } = useParams();
  const [orders,  setOrders]  = useState([]);
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(true);
  const [lastUp,  setLastUp]  = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/kitchen/${businessId}`);
      if (!res.ok) { setError('Kitchen not found.'); return; }
      setOrders(await res.json());
      setLastUp(new Date());
      setError('');
    } catch {
      setError('Network error — retrying…');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchOrders();
    const id = setInterval(fetchOrders, POLL_MS);
    return () => clearInterval(id);
  }, [fetchOrders]);

  const accepted       = orders.filter(o => o.status === 'ACCEPTED');
  const served         = orders.filter(o => o.status === 'SERVED');

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f1117',
      color: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      padding: '0 0 40px',
    }}>
      {/* Header */}
      <div style={{
        background: '#1a1d2e',
        borderBottom: '3px solid #FF6B35',
        padding: '14px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 28 }}>👨‍🍳</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 20, letterSpacing: -0.5 }}>
              Kitchen Display
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
              Auto-refreshes every 6s · {businessId}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <span style={{
              background: '#1e3a5f', border: '1px solid #3b82f6',
              color: '#60a5fa', padding: '4px 12px', borderRadius: 20,
              fontSize: 12, fontWeight: 700,
            }}>
              NEW · {accepted.length}
            </span>
            <span style={{
              background: '#1a3320', border: '1px solid #22c55e',
              color: '#4ade80', padding: '4px 12px', borderRadius: 20,
              fontSize: 12, fontWeight: 700,
            }}>
              SERVED · {served.length}
            </span>
          </div>
          {lastUp && (
            <div style={{ fontSize: 11, color: '#475569' }}>
              Updated {timeAgo(lastUp.toISOString())}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b', fontSize: 16 }}>
          Loading kitchen orders…
        </div>
      )}
      {error && (
        <div style={{
          margin: 24, background: '#2d1a1a', border: '1px solid #ef4444',
          borderRadius: 10, padding: '14px 18px', color: '#f87171', fontSize: 14,
        }}>
          {error}
        </div>
      )}

      {!loading && !error && orders.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#22c55e', marginBottom: 8 }}>
            All clear!
          </div>
          <div style={{ color: '#64748b', fontSize: 15 }}>
            No active orders in the kitchen right now.
          </div>
        </div>
      )}

      <div style={{ padding: '20px 20px 0' }}>

        {/* NEW / ACCEPTED orders */}
        {accepted.length > 0 && (
          <>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#3b82f6',
              textTransform: 'uppercase', letterSpacing: 1.5,
              marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: '#3b82f6',
                display: 'inline-block', animation: 'pulse 1.5s infinite',
              }} />
              Preparing — {accepted.length} order{accepted.length > 1 ? 's' : ''}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 16,
              marginBottom: 32,
            }}>
              {accepted.map(order => (
                <KitchenCard key={order.id} order={order} highlight />
              ))}
            </div>
          </>
        )}

        {/* SERVED orders */}
        {served.length > 0 && (
          <>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#22c55e',
              textTransform: 'uppercase', letterSpacing: 1.5,
              marginBottom: 14,
            }}>
              Served — Awaiting Payment · {served.length}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 14,
            }}>
              {served.map(order => (
                <KitchenCard key={order.id} order={order} highlight={false} />
              ))}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

function KitchenCard({ order, highlight }) {
  const elapsed = Math.floor((Date.now() - new Date(order.created_at)) / 60000);
  const urgent  = elapsed >= 15;
  const warn    = elapsed >= 8 && !urgent;

  return (
    <div style={{
      background: highlight ? '#111827' : '#0d1117',
      border: `2px solid ${highlight
        ? (urgent ? '#ef4444' : warn ? '#f59e0b' : '#3b82f6')
        : '#1e2535'}`,
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: highlight
        ? `0 0 0 1px ${urgent ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.15)'}`
        : 'none',
    }}>
      {/* Card header */}
      <div style={{
        background: highlight
          ? (urgent ? '#2d1a1a' : warn ? '#2d2200' : '#1e2a40')
          : '#111827',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${highlight ? '#1e3a5f' : '#1e2535'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            fontSize: 22, fontWeight: 900, color: highlight ? '#fff' : '#94a3b8',
          }}>
            #{order.id}
          </span>
          {order.table_no && (
            <span style={{
              background: '#1e2535', border: '1px solid #334155',
              color: '#94a3b8', padding: '3px 10px', borderRadius: 8,
              fontSize: 12, fontWeight: 600,
            }}>
              📍 {order.table_no}
            </span>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: 13, fontWeight: 700,
            color: urgent ? '#ef4444' : warn ? '#f59e0b' : '#64748b',
          }}>
            {elapsed}m ago
            {urgent && ' ⚠️'}
          </div>
          {order.order_type && order.order_type !== 'dine-in' && (
            <div style={{
              fontSize: 10, fontWeight: 700, color: '#f59e0b',
              textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2,
            }}>
              {order.order_type}
            </div>
          )}
        </div>
      </div>

      {/* Customer */}
      <div style={{
        padding: '8px 16px',
        borderBottom: '1px solid #1a2030',
        fontSize: 12, color: '#94a3b8',
      }}>
        👤 <b style={{ color: '#cbd5e1' }}>{order.customer_name || 'Guest'}</b>
        {order.notes && (
          <span style={{
            display: 'block', marginTop: 4,
            background: '#1e2a10', border: '1px solid #365314',
            borderRadius: 6, padding: '4px 8px',
            color: '#86efac', fontSize: 11,
          }}>
            📝 {order.notes}
          </span>
        )}
      </div>

      {/* Items */}
      <div style={{ padding: '10px 16px 14px' }}>
        {(order.items || []).map((item, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 0',
            borderBottom: i < order.items.length - 1 ? '1px solid #1a2030' : 'none',
          }}>
            <span style={{
              fontSize: 15, fontWeight: 600,
              color: highlight ? '#e2e8f0' : '#94a3b8',
            }}>
              {item.name}
            </span>
            <span style={{
              background: highlight ? '#1e3a5f' : '#1e2535',
              color: highlight ? '#60a5fa' : '#64748b',
              fontWeight: 800, fontSize: 16,
              padding: '2px 12px', borderRadius: 8, minWidth: 36, textAlign: 'center',
            }}>
              ×{item.quantity}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
